import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Table,
  Badge,
  InputGroup,
} from 'react-bootstrap';
import {
  FiDollarSign,
  FiRefreshCw,
  FiCalendar,
  FiTrendingUp,
  FiCpu,
  FiFileText,
  FiVolume2,
  FiActivity,
} from 'react-icons/fi';
import { costsApi } from '../services/api';
import { CostsSummary, CostsBreakdownResponse, CurrentPricing, CostPeriod } from '../types';
import { LoadingSpinner } from '../components/common';
import { useToast } from '../contexts/ToastContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// Default exchange rate USD to MXN (can be updated by user)
const DEFAULT_USD_TO_MXN = 17.50;

const formatUSD = (value: number): string => {
  if (value < 0.01) {
    return `$${value.toFixed(6)} USD`;
  }
  return `$${value.toFixed(4)} USD`;
};

const formatMXN = (value: number): string => {
  return `$${value.toFixed(2)} MXN`;
};

const formatNumber = (value: number): string => {
  return value.toLocaleString('es-MX');
};

const getMonthOptions = () => {
  const months = [];
  for (let i = 1; i <= 12; i++) {
    const date = new Date(2024, i - 1, 1);
    months.push({
      value: i,
      label: format(date, 'MMMM', { locale: es }),
    });
  }
  return months;
};

const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear; i >= currentYear - 2; i--) {
    years.push(i);
  }
  return years;
};

const CostsPage: React.FC = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<CostsSummary | null>(null);
  const [breakdown, setBreakdown] = useState<CostsBreakdownResponse | null>(null);
  const [pricing, setPricing] = useState<CurrentPricing | null>(null);
  const [exchangeRate, setExchangeRate] = useState<number>(DEFAULT_USD_TO_MXN);

  const [period, setPeriod] = useState<CostPeriod>('monthly');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);

  // Convert USD to MXN
  const toMXN = useCallback((usd: number) => usd * exchangeRate, [exchangeRate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryData, breakdownData, pricingData] = await Promise.all([
        costsApi.getSummary({ period, year, month }),
        costsApi.getBreakdown({ period, year, month, limit: 20 }),
        costsApi.getPricing(),
      ]);
      setSummary(summaryData);
      setBreakdown(breakdownData);
      setPricing(pricingData);
    } catch (error) {
      console.error('Error fetching costs data:', error);
      toast.error('Error', 'No se pudieron cargar los datos de costos');
    } finally {
      setLoading(false);
    }
  }, [period, year, month, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !summary) {
    return <LoadingSpinner message="Cargando datos de costos..." />;
  }

  const totalCostUSD = summary?.costs.total || 0;
  const totalCostMXN = toMXN(totalCostUSD);
  const avgCostUSD = summary?.averages.cost_per_interpretation || 0;
  const avgCostMXN = toMXN(avgCostUSD);

  const costByService = [
    { name: 'Interpretación IA', value: summary?.costs.gemini || 0, color: 'primary', icon: FiCpu },
    { name: 'Extracción PDF', value: summary?.costs.textract || 0, color: 'info', icon: FiFileText },
    { name: 'Lectura de Voz', value: summary?.costs.polly || 0, color: 'success', icon: FiVolume2 },
  ];

  return (
    <div className="costs-page">
      {/* Header */}
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <h2 className="mb-1">
              <FiDollarSign className="me-2" />
              Costos de Operación
            </h2>
            <p className="text-muted mb-0">
              Monitorea el consumo de servicios de IA y AWS
            </p>
          </div>
          <div className="d-flex align-items-center gap-3">
            <InputGroup size="sm" style={{ width: '180px' }}>
              <InputGroup.Text>USD/MXN</InputGroup.Text>
              <Form.Control
                type="number"
                step="0.01"
                min="1"
                value={exchangeRate}
                onChange={(e) => setExchangeRate(parseFloat(e.target.value) || DEFAULT_USD_TO_MXN)}
              />
            </InputGroup>
            <Button
              variant="outline-primary"
              onClick={fetchData}
              disabled={loading}
            >
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <FiRefreshCw />
              )}
              <span className="ms-2">Actualizar</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Period Selector */}
      <Card className="mb-4 filters-card">
        <Card.Body>
          <Row className="align-items-end g-3">
            <Col xs={12} md={3}>
              <Form.Label className="d-flex align-items-center gap-2">
                <FiCalendar />
                Periodo
              </Form.Label>
              <Form.Select
                value={period}
                onChange={(e) => setPeriod(e.target.value as CostPeriod)}
              >
                <option value="daily">Diario</option>
                <option value="weekly">Semanal</option>
                <option value="monthly">Mensual</option>
              </Form.Select>
            </Col>
            <Col xs={6} md={3}>
              <Form.Label>Año</Form.Label>
              <Form.Select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
              >
                {getYearOptions().map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={3}>
              <Form.Label>Mes</Form.Label>
              <Form.Select
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value))}
              >
                {getMonthOptions().map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label.charAt(0).toUpperCase() + m.label.slice(1)}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} md={3}>
              <Button variant="primary" onClick={fetchData} className="w-100">
                Consultar
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Main Stats Cards */}
      <Row className="g-4 mb-4">
        <Col xs={12} sm={6} lg={3}>
          <Card className="stat-card h-100 border-primary">
            <Card.Body>
              <div className="stat-icon text-primary">
                <FiDollarSign />
              </div>
              <div className="stat-value text-primary">
                {formatMXN(totalCostMXN)}
              </div>
              <div className="stat-label">Costo Total</div>
              <small className="text-muted">{formatUSD(totalCostUSD)}</small>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} lg={3}>
          <Card className="stat-card h-100">
            <Card.Body>
              <div className="stat-icon text-info">
                <FiTrendingUp />
              </div>
              <div className="stat-value">
                {summary?.total_interpretations || 0}
              </div>
              <div className="stat-label">Interpretaciones</div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} lg={3}>
          <Card className="stat-card h-100">
            <Card.Body>
              <div className="stat-icon text-warning">
                <FiActivity />
              </div>
              <div className="stat-value">
                {formatMXN(avgCostMXN)}
              </div>
              <div className="stat-label">Costo Promedio / Int.</div>
              <small className="text-muted">{formatUSD(avgCostUSD)}</small>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} lg={3}>
          <Card className="stat-card h-100">
            <Card.Body>
              <div className="stat-icon text-secondary">
                <FiCpu />
              </div>
              <div className="stat-value">
                {formatNumber(summary?.usage.gemini_calls || 0)}
              </div>
              <div className="stat-label">Llamadas IA</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Cost Breakdown by Service */}
      <Row className="g-4 mb-4">
        <Col xs={12} lg={6}>
          <Card className="h-100">
            <Card.Header className="d-flex align-items-center gap-2">
              <FiDollarSign />
              <span>Desglose por Servicio</span>
            </Card.Header>
            <Card.Body>
              {costByService.map((service) => {
                const percentage = totalCostUSD > 0 ? (service.value / totalCostUSD) * 100 : 0;
                const valueMXN = toMXN(service.value);
                return (
                  <div key={service.name} className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <div className="d-flex align-items-center gap-2">
                        <service.icon className={`text-${service.color}`} />
                        <span>{service.name}</span>
                      </div>
                      <div className="text-end">
                        <span className="fw-bold">{formatMXN(valueMXN)}</span>
                        <br />
                        <small className="text-muted">{formatUSD(service.value)}</small>
                      </div>
                    </div>
                    <div className="progress" style={{ height: '8px' }}>
                      <div
                        className={`progress-bar bg-${service.color}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <small className="text-muted">{percentage.toFixed(1)}% del total</small>
                  </div>
                );
              })}
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={6}>
          <Card className="h-100">
            <Card.Header className="d-flex align-items-center gap-2">
              <FiCpu />
              <span>Uso de Tokens (IA)</span>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col xs={6}>
                  <div className="text-center p-3">
                    <div className="display-6 text-primary">
                      {formatNumber(summary?.usage.gemini_input_tokens || 0)}
                    </div>
                    <div className="text-muted">Tokens de Entrada</div>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="text-center p-3">
                    <div className="display-6 text-success">
                      {formatNumber(summary?.usage.gemini_output_tokens || 0)}
                    </div>
                    <div className="text-muted">Tokens de Salida</div>
                  </div>
                </Col>
              </Row>
              <hr />
              <div className="text-center">
                <small className="text-muted">
                  Promedio por interpretación:{' '}
                  <strong>
                    {formatNumber(Math.round(summary?.averages.tokens_per_interpretation || 0))} tokens
                  </strong>
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Current Pricing */}
      <Row className="g-4 mb-4">
        <Col xs={12}>
          <Card>
            <Card.Header className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <FiDollarSign />
                <span>Precios Actuales</span>
              </div>
              {pricing && (
                <small className="text-muted">
                  Actualizado: {format(new Date(pricing.updated_at), 'dd/MM/yyyy HH:mm')}
                </small>
              )}
            </Card.Header>
            <Card.Body>
              <Row>
                <Col xs={12} md={6} lg={3} className="mb-3 mb-lg-0">
                  <div className="text-center p-3 border rounded h-100">
                    <FiCpu className="text-primary mb-2" size={24} />
                    <div className="text-primary fw-bold fs-5">
                      {formatMXN(toMXN(pricing?.prices.gemini_input_per_1m_tokens || 0))}
                    </div>
                    <small className="text-muted d-block">${pricing?.prices.gemini_input_per_1m_tokens.toFixed(2) || 'N/A'} USD</small>
                    <div className="mt-2 fw-medium">Análisis de Entrada</div>
                    <small className="text-muted">Costo por cada millón de palabras enviadas para interpretación</small>
                  </div>
                </Col>
                <Col xs={12} md={6} lg={3} className="mb-3 mb-lg-0">
                  <div className="text-center p-3 border rounded h-100">
                    <FiActivity className="text-primary mb-2" size={24} />
                    <div className="text-primary fw-bold fs-5">
                      {formatMXN(toMXN(pricing?.prices.gemini_output_per_1m_tokens || 0))}
                    </div>
                    <small className="text-muted d-block">${pricing?.prices.gemini_output_per_1m_tokens.toFixed(2) || 'N/A'} USD</small>
                    <div className="mt-2 fw-medium">Generación de Interpretación</div>
                    <small className="text-muted">Costo por cada millón de palabras generadas en la interpretación</small>
                  </div>
                </Col>
                <Col xs={12} md={6} lg={3} className="mb-3 mb-md-0">
                  <div className="text-center p-3 border rounded h-100">
                    <FiFileText className="text-info mb-2" size={24} />
                    <div className="text-info fw-bold fs-5">
                      {formatMXN(toMXN(pricing?.prices.textract_per_page || 0))}
                    </div>
                    <small className="text-muted d-block">${pricing?.prices.textract_per_page.toFixed(4) || 'N/A'} USD</small>
                    <div className="mt-2 fw-medium">Lectura de Documentos</div>
                    <small className="text-muted">Costo por cada página de PDF procesada</small>
                  </div>
                </Col>
                <Col xs={12} md={6} lg={3}>
                  <div className="text-center p-3 border rounded h-100">
                    <FiVolume2 className="text-success mb-2" size={24} />
                    <div className="text-success fw-bold fs-5">
                      {formatMXN(toMXN((pricing?.prices.polly_per_character || 0) * 1000))}
                    </div>
                    <small className="text-muted d-block">${((pricing?.prices.polly_per_character || 0) * 1000).toFixed(4)} USD</small>
                    <div className="mt-2 fw-medium">Lectura de Voz</div>
                    <small className="text-muted">Costo por cada 1,000 caracteres convertidos a audio</small>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Breakdown Table */}
      <Card className="breakdown-table-card">
        <Card.Header className="d-flex align-items-center gap-2">
          <FiFileText />
          <span>Desglose por Interpretación</span>
          <Badge bg="secondary" className="ms-auto">
            {breakdown?.count || 0} registros
          </Badge>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          ) : breakdown?.items && breakdown.items.length > 0 ? (
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>ID Interpretación</th>
                  <th>Fecha</th>
                  <th>Modo</th>
                  <th className="text-end">IA (MXN)</th>
                  <th className="text-end">PDF (MXN)</th>
                  <th className="text-end">Voz (MXN)</th>
                  <th className="text-end">Total (MXN)</th>
                  <th className="text-end">Total (USD)</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.items.map((item) => (
                  <tr key={item.interpretation_id}>
                    <td>
                      <code className="small">{item.interpretation_id.substring(0, 12)}...</code>
                    </td>
                    <td>
                      {format(new Date(item.created_at), 'dd/MM/yyyy HH:mm')}
                    </td>
                    <td>
                      <Badge bg={item.interpretation_mode === 'hierarchical' ? 'info' : 'secondary'}>
                        {item.interpretation_mode === 'hierarchical' ? 'completo' : 'simple'}
                      </Badge>
                    </td>
                    <td className="text-end">
                      <small>${toMXN(item.costs.gemini).toFixed(2)}</small>
                    </td>
                    <td className="text-end">
                      <small>${toMXN(item.costs.textract).toFixed(2)}</small>
                    </td>
                    <td className="text-end">
                      <small>${toMXN(item.costs.polly).toFixed(2)}</small>
                    </td>
                    <td className="text-end fw-bold text-primary">
                      {formatMXN(toMXN(item.costs.total))}
                    </td>
                    <td className="text-end">
                      <small className="text-muted">${item.costs.total.toFixed(4)}</small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-4 text-muted">
              No hay datos de costos para el periodo seleccionado
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default CostsPage;
